import React, { useEffect, useMemo, useReducer } from "react";
import { Button } from "@material-ui/core";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Add } from "@material-ui/icons";
import { useTranslation } from "react-i18next";

import { useAPIV2 } from "../../yoello-lib/modules/API2/useAPIv2";
import APIExtensions from "../../yoello-lib/modules/API2/APIDefs";
import { ICallAPIFunction } from "../../yoello-lib/modules/API2/types";
import FilterComponent from "../../yoello-lib/modules/components/filterComponent/FilterComponent";
import { updateMenuItemPositions, setFilterState, setMenuItems } from "./state";
import { OrderListHeaders } from "../orders/OrderList/OrderListViewHeaders";
import { ProductListView } from "../menu/products/productList/ProductListView";
import { manageMenuColumns } from "./columns";
import { ManageMenuReducer } from "./state/ManageMenu";
import { IManageMenuItemsState, IMenuData } from "./types";
import IMenuItemBody from "../../interfaces/IMenuItemBody";
import ISubCategory from "../menu/categories/ISubCategory";
import { onApplyFilters } from "../menu/products/utils";
import Styles from "../../scss/discounts.module.scss";
import { useStyles } from "./styles"
import { MenuStatus } from './ui/MenuStatus';
import { useDialogRouteStages } from "../dialogRoutes/useDialogRouteStages";
import { useHistory } from "react-router-dom";
import { CREATE_URL } from "../../const/URL.const";
import DisabledActionWrapper from "../../yoello-lib/modules/components/DisabledActionWrapper";


interface IManageMenuItemContainer {
    menusList: IMenuData[];
    isLoading: boolean;
    manageMenuApi: ICallAPIFunction<IMenuData>;
    venueId: string;
    menuItemsList: IMenuItemBody;
    subcategories: ISubCategory[];
    path: string;
    hasPermission?: boolean;
    onFailedInteraction?: Function;
}
export const manageMenuInitalState: IManageMenuItemsState = {
    filterState: [],
    filterList: [],
    menuLists: [],
    menuFormData: {
        description: '',
        enabled: false,
        name_public: '',
        name_private: '',
        selectedMenuItemIDs: []
    },
    selectedMenuItemsList: []
};

export function ManageMenuItemContainer({
    menusList,
    isLoading,
    manageMenuApi,
    venueId,
    menuItemsList,
    subcategories,
    path,
    hasPermission,
    onFailedInteraction
}: IManageMenuItemContainer) {
    const history = useHistory();
    const { enableSpecificMenuUrl } = useFlags();
    const classes = useStyles();
    const [state, dispatch] = useReducer(
        ManageMenuReducer,
        manageMenuInitalState
    );
    const { filterState, menuLists } = state;
    const { goToNextStep } = useDialogRouteStages(history);
    const { t } = useTranslation();

    const { callAPI: sortMenu } = useAPIV2(APIExtensions.menus, {
        onMountDisabled: true,
        errorCall: t("menu.menuItems.sortFail"),
        successCall: t("menu.menuItems.sortSuccess"),
        query: "sort",
        yoelloAPIParams: { venue_id: venueId, size: 200, include_disabled: true },
        isPaginated: true
    });

    useEffect(() => {
        dispatch(setMenuItems(menusList as IMenuData[]));
    }, [menusList]);

    const listData = useMemo(() => {
        if (filterState.length > 0) {
            const selectedFilters = {
                active: filterState[0].values.includes("active"),
                inActive: filterState[0].values.includes("inActive")
            }
            return onApplyFilters(menuLists, selectedFilters, getFilters())
        }
        return menuLists;
        //eslint-disable-next-line
    }, [filterState, menuLists]);


    function openCreateMenuDialog() {
        goToNextStep("menu", {
            ComponentProps: {
                venueId,
                menusList,
                menuItemsList,
                itemData: state.menuFormData,
                subcategories: subcategories
            },
            dialogTitle: t('manageMenu.manageMenuContainer.create'),
        }, `${path}/${CREATE_URL}`);
    }

    async function openEditMenuDialog(item: any) {
        const newData: IMenuData = {
            ...item,
        }
        goToNextStep("menu", {
            ComponentProps: {
                venueId,
                menusList,
                itemData: newData,
                menuItemsList,
                subcategories: subcategories,
                enableSpecificMenuUrl : enableSpecificMenuUrl
            },
            dialogTitle: t('manageMenu.manageMenuContainer.update', { menu_name: newData.name_public }),
        }, `${path}/${newData.id}`);
    }

    function getFilters() {
        const { filterState } = state;
        const filters: {
            key: string,
            values: string[]
        } | undefined = filterState[0];
        return [
            {
                key: "menu_status",
                label: "filters.menuStatus",
                filters: [
                    {
                        key: "active",
                        label: "filters.activeMenu",
                        param: "enabled",
                        checked: filters ? filters.values.includes("active") : false,
                        expected: true,
                    },
                    {
                        key: "inActive",
                        label: "filters.inActiveMenu",
                        param: "enabled",
                        checked: filters ? filters.values.includes("inActive") : false,
                        expected: false,
                    },
                ],
            }
        ]
    }
    function setFilters(filterSubmitData: any) {
        dispatch(setFilterState(filterSubmitData));
    }
    function updateMenuItemPosition(itemPosition: string[]) {
        dispatch(updateMenuItemPositions(itemPosition));
    }
    return <>
        {
            !isLoading && <div className={Styles.controlers}>
                <div>
                    <DisabledActionWrapper
                        disabled={!hasPermission}
                        onDisabledClick={onFailedInteraction}
                    >
                        <Button onClick={openCreateMenuDialog}
                            size="large"
                            variant="contained"
                            color="primary"
                            data-component-name="CREATE_NEW_MENU"
                            startIcon={<Add />}
                            disabled={!hasPermission}
                        >
                            {t('manageMenu.manageMenuContainer.create')}
                        </Button>
                    </DisabledActionWrapper>
                    
                </div>
                <span className={Styles.divider}></span>
                <div className={Styles.filter}>
                    <FilterComponent filtersList={getFilters()} filterkey={`menusFilters-${venueId}`}
                        setFilterState={setFilters}
                    />
                </div>
            </div>
        }

        <div data-component-name="MenusListContainer">
            <div key={`columnHeaders-manageMenuListItems`} className={`${Styles.columnsHeader} ${classes.headers}`}>
                {manageMenuColumns.map((column) => <OrderListHeaders {...column} key={`col-${column.key}-${column.label}`} />)}
            </div>
            <ProductListView
                dataComponentName="MenusList"
                listClassName={classes.draggableMenuItems}
                isLoading={isLoading}
                productList={listData}
                onSortedItem={updateMenuItemPosition}
                onItemSelection={openEditMenuDialog}
                sortAPICall={sortMenu}
                parentEntry={[]}
                emptyEntryText={t('manageMenu.manageMenuContainer.emptyList')}
                emptyEntryTextInfo={t('manageMenu.manageMenuContainer.emptyListInfo')}
                emptyListText={t('manageMenu.manageMenuContainer.emptyList')}
                emptyListTextInfo={t('manageMenu.manageMenuContainer.emptyListInfo')}
                fieldList={[
                    {
                        key: "name_public",
                    },
                    {
                        key: "description"
                    },
                    {
                        key: "enabled",
                        modifier: (enabled: boolean) => {
                            return <MenuStatus enabled={enabled} />
                        }
                    },

                ]}
            />
        </div>
    </>
}
